import {useRef, useState} from 'react';

import {useOnMount} from 'shared/web';

import type {PdfExportResult} from '../types/PdfExportResult';
import {PdfExportError} from '../utils/PdfExportError';

type ExportState =
  | {status: 'loading'}
  | {status: 'error'; messageKey: string; retryable: boolean; recoveryMessageKey?: string}
  | {status: 'ready'; result: PdfExportResult; url: string};

// A download may still be reading its Blob when the dialog closes.
const URL_RELEASE_DELAY_MS = 60_000;
const releaseUrl = (url: string) => {
  window.setTimeout(() => URL.revokeObjectURL(url), URL_RELEASE_DELAY_MS);
};

export function usePdfExport(create: (signal: AbortSignal) => Promise<PdfExportResult>) {
  const [state, setState] = useState<ExportState>({status: 'loading'});
  const pending = useRef<AbortController | null>(null);
  const url = useRef<string | null>(null);

  const generate = () => {
    if (state.status === 'error' && !state.retryable) {
      return;
    }
    if (pending.current !== null && !pending.current.signal.aborted) {
      return;
    }
    const controller = new AbortController();
    pending.current = controller;
    setState({status: 'loading'});
    void Promise.resolve()
      .then(() => {
        controller.signal.throwIfAborted();
        return create(controller.signal);
      })
      .then((result) => {
        if (controller.signal.aborted) {
          return;
        }
        if (result.bytes.length === 0 || result.filename.trim().length === 0) {
          throw new Error('PDF export returned an empty document or filename');
        }
        if (url.current !== null) {
          releaseUrl(url.current);
        }
        const nextUrl = URL.createObjectURL(
          new Blob([Uint8Array.from(result.bytes)], {type: 'application/pdf'})
        );
        url.current = nextUrl;
        setState({status: 'ready', result, url: nextUrl});
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setState(
            error instanceof PdfExportError
              ? {
                  status: 'error',
                  messageKey: error.messageKey,
                  retryable: error.retryable,
                  recoveryMessageKey: error.recoveryMessageKey,
                }
              : {status: 'error', messageKey: 'general.labels.somethingWentWrong', retryable: true}
          );
        }
      })
      .finally(() => {
        if (pending.current === controller) {
          pending.current = null;
        }
      });
  };

  useOnMount(generate, () => {
    pending.current?.abort();
    if (url.current !== null) {
      releaseUrl(url.current);
      url.current = null;
    }
  });

  return {state, generate};
}
